import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@shared/lib/api";
import type {
  Child,
  Classroom,
  Conversation,
  Message,
  ParentUser,
} from "@shared/lib/types";

export type MessagingView = "conversations" | "thread" | "compose";
export type DesktopMessageType = "child" | "broadcast";
export type DesktopRecipientType = "child" | "parent";

type UseDesktopMessagingOptions = {
  enabled?: boolean;
  canCompose: boolean;
  onConversationsRefresh?: () => void;
  onConversationRead?: () => void;
};

export function useDesktopMessaging({
  enabled = true,
  canCompose,
  onConversationsRefresh,
  onConversationRead,
}: UseDesktopMessagingOptions) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<MessagingView>("conversations");
  const [activeConversation, setActiveConversation] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);

  const [messageType, setMessageType] = useState<DesktopMessageType>("child");
  const [selectedChildId, setSelectedChildId] = useState<number | null>(null);
  const [selectedParentUserId, setSelectedParentUserId] = useState<number | null>(null);
  const [selectedRecipientType, setSelectedRecipientType] =
    useState<DesktopRecipientType | null>(null);
  const [selectedRecipientLabel, setSelectedRecipientLabel] = useState("");
  const [selectedClassroomId, setSelectedClassroomId] = useState<number | null>(null);
  const [composeBody, setComposeBody] = useState("");
  const [composeSending, setComposeSending] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);
  const [children, setChildren] = useState<Child[]>([]);
  const [parents, setParents] = useState<ParentUser[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [composeDataLoading, setComposeDataLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);

  const fetchConversations = useCallback(async () => {
    if (!enabled) return;

    setLoading(true);
    try {
      const res = await apiFetch<{ conversations: Conversation[] }>(
        "/api/v1/messages/conversations"
      );
      setConversations(res.data.conversations);
      onConversationsRefresh?.();
    } catch {
      // Messaging is non-critical; keep the existing quiet failure behavior.
    } finally {
      setLoading(false);
    }
  }, [enabled, onConversationsRefresh]);

  const fetchMessages = useCallback(async () => {
    if (!enabled || !activeConversation) return;

    setMessagesLoading(true);
    try {
      const res = await apiFetch<{ messages: Message[] }>(
        `/api/v1/messages?conversation_key=${encodeURIComponent(activeConversation)}`
      );
      setMessages(res.data.messages);
    } catch {
      // Keep the thread open if a poll/fetch fails.
    } finally {
      setMessagesLoading(false);
    }
  }, [activeConversation, enabled]);

  const fetchComposeData = useCallback(async () => {
    if (!enabled || !canCompose) return;

    setComposeDataLoading(true);
    try {
      const [childrenRes, classroomsRes, parentsRes] = await Promise.all([
        apiFetch<{ children: Child[] }>("/api/v1/children"),
        apiFetch<{ classrooms: Classroom[] }>("/api/v1/classrooms"),
        apiFetch<{ parents: ParentUser[] }>("/api/v1/parents?per_page=100&view=list"),
      ]);
      setChildren(childrenRes.data.children);
      setClassrooms(classroomsRes.data.classrooms);
      setParents(parentsRes.data.parents);
    } catch {
      // Compose data can be retried by reopening the composer.
    } finally {
      setComposeDataLoading(false);
    }
  }, [canCompose, enabled]);

  useEffect(() => {
    if (enabled) {
      void fetchConversations();
    }
  }, [enabled, fetchConversations]);

  useEffect(() => {
    if (enabled && view === "compose") {
      void fetchComposeData();
    }
  }, [enabled, fetchComposeData, view]);

  useEffect(() => {
    if (enabled && view === "thread") {
      void fetchMessages();
    }
  }, [activeConversation, enabled, fetchMessages, view]);

  const selectConversation = useCallback(
    async (key: string) => {
      setActiveConversation(key);
      setView("thread");
      setConversations((current) =>
        current.map((conversation) =>
          conversation.conversation_key === key
            ? { ...conversation, unread_count: 0 }
            : conversation
        )
      );

      try {
        await apiFetch(`/api/v1/conversations/${encodeURIComponent(key)}/mark_read`, {
          method: "POST",
        });
        onConversationRead?.();
        window.dispatchEvent(new CustomEvent("messages:unread-changed"));
      } catch {
        // Opening the thread should not depend on mark-read succeeding.
      }
    },
    [onConversationRead]
  );

  const sendReply = useCallback(async (): Promise<boolean> => {
    if (!replyBody.trim() || !activeConversation || sending) return false;

    const lastMessage = messages[0];
    if (!lastMessage) return false;

    setSending(true);
    try {
      await apiFetch("/api/v1/messages", {
        method: "POST",
        body: {
          message: {
            body: replyBody.trim(),
            reply_to_message_id: lastMessage.id,
            child_id: lastMessage.child?.id,
            classroom_id: lastMessage.classroom?.id,
            parent_user_id: lastMessage.parent_user_id,
          },
        },
      });
      setReplyBody("");
      await fetchMessages();
      return true;
    } catch {
      return false;
    } finally {
      setSending(false);
    }
  }, [activeConversation, fetchMessages, messages, replyBody, sending]);

  const resetCompose = useCallback(() => {
    setComposeBody("");
    setSelectedChildId(null);
    setSelectedParentUserId(null);
    setSelectedRecipientType(null);
    setSelectedRecipientLabel("");
    setSelectedClassroomId(null);
    setSearchQuery("");
    setMessageType("child");
    setComposeError(null);
  }, []);

  const sendComposedMessage = useCallback(async (): Promise<boolean> => {
    setComposeError(null);

    if (!composeBody.trim()) {
      setComposeError("Please enter a message");
      return false;
    }
    if (messageType === "child" && !selectedRecipientType) {
      setComposeError("Please search and select a recipient");
      return false;
    }
    if (messageType === "broadcast" && !selectedClassroomId) {
      setComposeError("Please select a classroom");
      return false;
    }

    setComposeSending(true);
    try {
      const payload: Record<string, unknown> = {
        body: composeBody.trim(),
        is_broadcast: messageType === "broadcast",
      };

      if (messageType === "child") {
        if (selectedRecipientType === "child") {
          payload.child_id = selectedChildId;
        } else if (selectedRecipientType === "parent") {
          payload.parent_user_id = selectedParentUserId;
        }
      } else {
        payload.classroom_id = selectedClassroomId;
      }

      const res = await apiFetch<{ message: Message }>("/api/v1/messages", {
        method: "POST",
        body: { message: payload },
      });

      const newKey = res.data.message.conversation_key;
      if (newKey) {
        setActiveConversation(newKey);
        setView("thread");
      } else {
        setView("conversations");
      }

      resetCompose();
      void fetchConversations();
      return true;
    } catch (err) {
      setComposeError(err instanceof Error ? err.message : "Failed to send message");
      return false;
    } finally {
      setComposeSending(false);
    }
  }, [
    composeBody,
    fetchConversations,
    messageType,
    resetCompose,
    selectedChildId,
    selectedClassroomId,
    selectedParentUserId,
    selectedRecipientType,
  ]);

  const openCompose = useCallback(() => {
    setView("compose");
    setActiveConversation(null);
  }, []);

  return {
    conversations,
    loading,
    view,
    setView,
    activeConversation,
    setActiveConversation,
    messages,
    messagesLoading,
    replyBody,
    setReplyBody,
    sending,
    messageType,
    setMessageType,
    selectedChildId,
    setSelectedChildId,
    selectedParentUserId,
    setSelectedParentUserId,
    selectedRecipientType,
    setSelectedRecipientType,
    selectedRecipientLabel,
    setSelectedRecipientLabel,
    selectedClassroomId,
    setSelectedClassroomId,
    composeBody,
    setComposeBody,
    composeSending,
    composeError,
    children,
    parents,
    classrooms,
    composeDataLoading,
    searchQuery,
    setSearchQuery,
    showSearchResults,
    setShowSearchResults,
    fetchConversations,
    selectConversation,
    sendReply,
    sendComposedMessage,
    openCompose,
  };
}
